/**
 * Il lato RUNTIME della sessione Limulo: mette insieme le funzioni pure di `auth.ts` con
 * il browser vero — `localStorage`, `fetch`, l'orologio, e l'identita' Logto corrente.
 *
 * La divisione e' deliberata. In `auth.ts` c'e' tutto cio' che si puo' provare senza rete
 * e senza token; qui c'e' solo il cablaggio, tenuto sottile apposta perche' e' la parte
 * che non e' esercitata da `tools/verify-limulo.ts`.
 *
 * Da `src/lib/logto.ts` leggiamo il `sub` corrente e il raw ID token gia' persistito dal
 * bridge. Il token resta opaco qui: firma, issuer, audience e claim vengono verificati dal
 * BFF. Il legame fra sessione Limulo e identita' Logto resta difeso anche da `boundTo`.
 */
import { env } from '@/env';
import {
  getLogtoAccessToken,
  getMyLogtoSub,
  getStoredIdentityToken,
} from '@/lib/logto';
import { storage } from '@/lib/storage';
import { fetchWithTimeout, TOKEN_FETCH_TIMEOUT_MS } from '@/lib/fetchWithTimeout';

import {
  clearStoredSession,
  exchangeExternalToken,
  needsRefresh,
  readStoredSession,
  refreshLimuloSession,
  writeStoredSession,
  isTerminalRefusal,
  rememberSessionFailure,
  type LimuloAuthDeps,
  type LimuloAuthResult,
  type LimuloSession,
} from './auth';
import type { LimuloFailure } from './errors';

/**
 * L'identita' a cui legare la sessione. Un utente non ancora identificato ha `''`: e'
 * un valore legittimo, e vuol dire che la sessione ottenuta ora verra' scartata da sola
 * quando l'utente si identifichera' — che e' esattamente il comportamento voluto.
 */
const currentBoundTo = (): string => getMyLogtoSub() ?? '';

/**
 * 🔴 La scadenza di questa catena e' un PRESTITO, e va detto perche' resti falsificabile.
 *
 * `TOKEN_FETCH_TIMEOUT_MS` (15 s) e' giustificata da 16 campioni di `POST /oidc/token`
 * (mediana 373 ms, p90 908 ms). L'exchange di Limulo e' un **endpoint diverso**, e riusare
 * quella costante senza dirlo vorrebbe dire prendere in prestito anche la sua giustificazione.
 *
 * ⚠️ Ho provato a misurarlo e NON SI PUO', oggi: nei `network.json` gia' registrati dal runner
 * e2e ci sono **4** richieste a `/v1/auth/exchange-token` e **nessuna** porta una durata
 * (verificato il 2026-09-06 su tutte le run in `~/.tma-e2e/runs`). Zero campioni utili.
 *
 * ⇒ Cosa regge il prestito, dichiarato: due endpoint DIVERSI della stessa piattaforma,
 * misurati dallo stesso runner, stanno entrambi nella banda dei centesimi di secondo —
 * `/oidc/token` (mediana 373 · max 980 ms) e l'exchange Laravel (n=24: mediana 304 · p90 360
 * · max 874 ms). 15 s sono ~17× il massimo peggiore fra i due. ⚠️ Il BFF Limulo pero'
 * **inoltra a un provider**, quindi puo' legittimamente essere piu' lento di un endpoint di
 * token: e' esattamente per questo che questa resta una SCELTA e non una misura.
 * ⇒ Quando il runner registrera' una durata su questa rotta, questo numero si rimisura.
 */
const LIMULO_FETCH_TIMEOUT_MS = TOKEN_FETCH_TIMEOUT_MS;

const deps = (): LimuloAuthDeps => ({
  // ⛔ MAI il `fetch` globale nudo. Senza scadenza, un exchange che resta pendente non
  // produce ne' errore ne' risposta: l'utente aspetta la rete di sicurezza dei 45 s
  // (`hooks/useStalled.ts`) invece di un verdetto. E' la stessa classe di guasto chiusa in
  // #176 per la catena Logto; questa e' l'altra catena, che #176 non tocca.
  // ⚠️ Nessun cast sul risultato, di proposito: `Response` soddisfa `LimuloHttpResponse`
  // strutturalmente (`status: number`, `json(): Promise<any>`). Un cast qui nasconderebbe il
  // giorno in cui non lo soddisfacesse piu'.
  // ✅ E il lancio alla scadenza e' SICURO qui, misurato e non assunto: l'unico punto che usa
  // `deps.fetch` e' `postJson` (`auth.ts:184`), che e' gia' dentro un `try/catch` e trasforma
  // un rigetto nel verdetto `FETCH_ERROR` (`auth.ts:196-200`) — un esito onesto, non
  // un'eccezione che sfugge.
  fetch: (url, init) =>
    fetchWithTimeout(fetch, url, init as RequestInit, LIMULO_FETCH_TIMEOUT_MS),
  baseUrl: env.limuloApiUrl,
  now: () => Date.now(),
  boundTo: currentBoundTo(),
  organizationId: env.limuloOrgId,
});

/** La sessione com'e' a riposo, senza toccare la rete. Per le schermate diagnostiche. */
export function peekLimuloSession(): LimuloSession | null {
  return readStoredSession(storage, { now: Date.now(), boundTo: currentBoundTo() });
}

/**
 * Scambia un token esterno con la sessione Limulo e la persiste.
 *
 * La diagnostica dev puo' ancora passare un token a mano. Il percorso servito usa invece
 * `startLimuloSessionFromCurrentLogto`, che fornisce anche il raw ID token corrente.
 */
export async function startLimuloSession(
  externalToken: string,
  identityToken?: string,
): Promise<LimuloAuthResult> {
  const requestDeps = deps();
  const result = await exchangeExternalToken(externalToken, requestDeps, identityToken);
  // Un logout/cambio account puo' avvenire mentre l'exchange e' in volo. Non riscriviamo
  // nel browser una sessione nata per l'identita' precedente dopo che il teardown l'ha
  // appena eliminata.
  if (result.ok && currentBoundTo() === requestDeps.boundTo) {
    writeStoredSession(storage, result.session);
  }
  return result;
}

/**
 * L'esito dell'ULTIMO tentativo di creare una sessione, quando e' fallito.
 *
 * 🔴 Esiste perche' qui si perdeva la causa. `startLimuloSessionFromCurrentLogto` ritorna
 * `LimuloSession | null`, e quel `null` e' lo stesso per «il server ha rifiutato il token» e
 * per «ci ho rinunciato io dopo 15 secondi». A valle, senza header, il server risponde 401 —
 * e ha ragione — quindi la schermata diceva «Identita' non valida, serve una nuova
 * autenticazione» per una richiesta che bastava riprovare.
 * ⛔ Non e' un rimedio a valle: e' il RICORDO di cio' che solo questo punto ha visto.
 */
let lastSessionFailure: LimuloFailure | null = null;

/** L'ultimo fallimento di sessione, per chi deve spiegare un 401 che ne discende. */
export function peekLastSessionFailure(): LimuloFailure | null {
  return lastSessionFailure;
}

async function startLimuloSessionFromCurrentLogto(): Promise<LimuloSession | null> {
  // Questa chiamata puo' rinnovare l'access token e, nello stesso grant, persiste anche
  // un ID token aggiornato. Per questo il raw ID token si legge DOPO aver ottenuto
  // l'access token, non prima.
  const externalToken = await getLogtoAccessToken();
  if (!externalToken) return null;
  const identityToken = getStoredIdentityToken();
  const result = await startLimuloSession(
    externalToken,
    identityToken ?? undefined,
  );
  // ⚠️ Si azzera sul successo: un fallimento vecchio che spiegasse un 401 nuovo sarebbe
  // un'altra causa sbagliata raccontata con sicurezza. La regola sta in `auth.ts` perche' li'
  // e' esercitabile — qui una mutazione che la rompeva passava verde.
  lastSessionFailure = rememberSessionFailure(result);
  return result.ok && result.session.boundTo === currentBoundTo()
    ? result.session
    : null;
}

export function endLimuloSession(): void {
  clearStoredSession(storage);
}

/**
 * «Esci»: revoca la sessione Limulo SUL SERVER e poi la toglie dal telefono.
 *
 * 🔴 Prima di questa funzione il logout cancellava solo `qlash.limulo.session` (teardown
 * centrale `PER_SERVICE_TOKEN_KEYS`, `logto.ts`): la `QlashMobileSession` restava viva per la
 * durata del refresh (30-60 giorni) e il suo JWT e' un token di BACKOFFICE (`auth.ts:18-21`).
 * Reperto 07/10 01:28Z: una sessione di collaudo finita in un transcript l'ha dovuta revocare
 * a mano chi l'ha trovata.
 *
 * ⚖️ MIGLIOR SFORZO, mai una condizione del logout:
 *  · la POST parte PRIMA di toccare lo storage (il refresh token si legge prima che sparisca) e
 *    la chiave si toglie SUBITO dopo, di sincrono: l'esito della rete non decide niente;
 *  · qualunque esito (2xx, 4xx, 5xx, scadenza, rete giu', un throw) e' inghiottito: la promessa
 *    ritornata NON rifiuta mai e si risolve al piu' alla scadenza di `fetchWithTimeout`;
 *  · nessun retry: un secondo tentativo, a sessione gia' tolta dal telefono, non avrebbe piu'
 *    il refresh token da mandare.
 *
 * ⚠️ Chi chiama puo' NON attendere la promessa (la chiave e' gia' sparita) ma chi sta per
 * ricaricare la pagina farebbe bene ad attenderla: un reload abbandona la richiesta in volo.
 *
 * ⛔ Non passa dalla mutation RTK `postLimuloLogout`: il suo `prepareHeaders` chiama
 * `getLimuloToken()`, che a sessione scaduta RINNOVA — cioe' ruota il refresh token mentre lo
 * stiamo buttando. Qui il solo materiale e' il refresh token, nel CORPO (`mobile-auth.js:973`):
 * senza header `Authorization`. Il server risponde 200 anche quando non c'e' niente da revocare.
 */
export function revocaEChiudiLimuloSession(): Promise<void> {
  let richiesta: Promise<void> = Promise.resolve();
  try {
    const session = peekLimuloSession();
    const refreshToken = session?.refreshToken?.trim();
    if (refreshToken) {
      const { fetch: invia, baseUrl } = deps();
      richiesta = invia(`${baseUrl}/v1/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        // Come le altre due POST della catena (`auth.ts` `postJson`): i cookie del backoffice
        // non c'entrano e mandarli allargherebbe la superficie senza servire a niente.
        credentials: 'omit',
        body: JSON.stringify({ refreshToken }),
      }).then(
        () => undefined,
        () => undefined,
      );
    }
  } catch {
    // Un throw SINCRONO (storage illeggibile, `deps()`) non deve fermare il logout.
  }
  endLimuloSession();
  return richiesta;
}

/**
 * Un solo rinnovo alla volta.
 *
 * Senza questa serializzazione, N chiamate RTK Query che partono insieme con un token
 * scaduto fanno N refresh — e siccome il server RUOTA il refresh token a ogni giro
 * (`mobile-auth.js:707-710`), il primo che torna invalida quello che gli altri hanno in
 * mano: si finisce con una sessione morta partendo da una sessione sana.
 */
let inFlightRefresh: Promise<LimuloSession | null> | null = null;
let inFlightExchange: Promise<LimuloSession | null> | null = null;

const runRefresh = async (session: LimuloSession): Promise<LimuloSession | null> => {
  const result = await refreshLimuloSession(session.refreshToken, deps());
  if (!result.ok) {
    if (isTerminalRefusal(result.failure)) {
      // Rifiuto vero: si butta la sessione invece di lasciarla a produrre 401 a ogni
      // schermata.
      clearStoredSession(storage);
      return null;
    }
    // Transitorio: la sessione RESTA. Questo giro non ha un token — le schermate
    // degradano — e il prossimo tentativo riparte dallo stesso refresh, che il server
    // non ha mai guardato.
    return null;
  }
  writeStoredSession(storage, result.session);
  return result.session;
};

/**
 * Come e' andato il rinnovo forzato dopo un 401 (`rinnovaDopo401`).
 *
 *  · `rinnovata` — c'e' un token nuovo (o l'aveva gia' messo un'altra richiesta): si ripete.
 *  · `tolta`     — la sessione non c'e' piu' (rifiuto TERMINALE del refresh, o gia' tolta da
 *                  un'altra richiesta): si ripete, e `getLimuloToken` rifa' l'exchange.
 *  · `transitorio` — il refresh non e' arrivato a un verdetto (rete, 5xx): la sessione RESTA,
 *                  non si ripete, il 401 arriva alla schermata com'e'.
 */
export type EsitoRinnovoDopo401 = 'rinnovata' | 'tolta' | 'transitorio';

/**
 * UN rinnovo forzato dopo un 401 su una richiesta che PORTAVA `Authorization`.
 *
 * 🔴 Perche' esiste: `getLimuloToken` rinnova solo quando l'access token sta per scadere IN
 * LOCALE (`needsRefresh`). Una sessione REVOCATA dal server dichiara ancora ~27 giorni di
 * validita': per il client e' sana, il server risponde 401 «Sessione mobile revocata» a ogni
 * richiesta, e nulla la toglie — finche' qualcuno cancella a mano `qlash.limulo.session`
 * (misurato nel collaudo di staging del 09/10, cantiere 16 di qbridge-orc).
 *
 * ⚖️ Non e' logica nuova di refresh: riusa `inFlightRefresh` e `runRefresh`, cioe' la
 * serializzazione (il server RUOTA il refresh a ogni giro) e la distinzione terminale/transitorio
 * (`isTerminalRefusal`) di sempre.
 *
 * ⚠️ `tokenMandato` e' il Bearer che la richiesta fallita portava davvero. Serve a una cosa:
 * se la sessione in storage ha GIA' un token diverso, un'altra richiesta (o `getLimuloToken`) l'ha
 * rinnovata fra l'invio e questo 401 — rinnovare ancora ruoterebbe il refresh per niente, e le
 * richieste che tornano a 401 UNA ALLA VOLTA farebbero N rinnovi invece di uno.
 * ⛔ Il token non esce da qui: non e' loggato, non e' nel testo di un errore.
 */
export async function rinnovaDopo401(tokenMandato: string): Promise<EsitoRinnovoDopo401> {
  const session = peekLimuloSession();
  if (!session) return 'tolta';
  if (session.token !== tokenMandato) return 'rinnovata';

  inFlightRefresh ??= runRefresh(session).finally(() => {
    inFlightRefresh = null;
  });
  const refreshed = await inFlightRefresh;
  if (refreshed) return 'rinnovata';
  // `runRefresh` torna `null` sia per il rifiuto terminale (che ha TOLTO la sessione) sia per
  // il transitorio (che l'ha lasciata): lo storage dice quale dei due.
  return peekLimuloSession() === null ? 'tolta' : 'transitorio';
}

/**
 * Il token da mettere in `Authorization`, rinnovato se sta per scadere. `null` quando non
 * c'e' una sessione utilizzabile — e in quel caso il client NON manda un header vuoto:
 * un `Bearer ` senza valore produce un 401 che somiglia a un problema di permessi.
 */
export async function getLimuloToken(): Promise<string | null> {
  const session = peekLimuloSession();
  if (!session) {
    // L'apertura di una schermata puo' far partire piu' query insieme. Un solo exchange
    // deve creare la sessione; tutte le altre aspettano la stessa promise.
    inFlightExchange ??= startLimuloSessionFromCurrentLogto().finally(() => {
      inFlightExchange = null;
    });
    const started = await inFlightExchange;
    return started?.token ?? null;
  }
  if (!needsRefresh(session, Date.now())) return session.token;

  inFlightRefresh ??= runRefresh(session).finally(() => {
    inFlightRefresh = null;
  });
  const refreshed = await inFlightRefresh;
  return refreshed?.token ?? null;
}
